import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import getCroppedImg from '@/lib/cropImage';
import { Loader2, ZoomIn, ZoomOut, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface AvatarEditorProps {
    imageSrc: string;
    onCancel: () => void;
    onSave: (blob: Blob) => Promise<void> | void;
}

export const AvatarEditor: React.FC<AvatarEditorProps> = ({ imageSrc, onCancel, onSave }) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<{ x: number, y: number, width: number, height: number } | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);

    const onCropComplete = useCallback((_: any, croppedAreaPixels: { x: number, y: number, width: number, height: number }) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleSave = async () => {
        if (!croppedAreaPixels) return;
        setIsProcessing(true);
        try {
            const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
            if (croppedImage) {
                // Await the save operation (upload)
                // If it succeeds, the parent should unmount this component or we can close it.
                // But the parent handles the logic.
                await onSave(croppedImage);
            }
        } catch (e) {
            console.error(e);
            // Optional: Show error in UI?
            // Since this component is just an editor, we rely on parent to alert or we assume onSave handled alert.
        }
        setIsProcessing(false);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col h-[80vh] md:h-[600px]">
                <div className="p-4 border-b flex justify-between items-center">
                    <h3 className="font-bold text-lg">アイコン調整</h3>
                    <button onClick={onCancel} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <div className="relative flex-1 bg-gray-900">
                    <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        aspect={1}
                        onCropChange={setCrop}
                        onCropComplete={onCropComplete}
                        onZoomChange={setZoom}
                        cropShape="round"
                        showGrid={false}
                    />
                </div>

                <div className="p-6 space-y-4 bg-white">
                    <div className="flex items-center gap-4">
                        <ZoomOut className="w-4 h-4 text-gray-400" />
                        <input
                            type="range"
                            value={zoom}
                            min={1}
                            max={3}
                            step={0.1}
                            aria-labelledby="Zoom"
                            onChange={(e) => setZoom(Number(e.target.value))}
                            className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-black"
                        />
                        <ZoomIn className="w-4 h-4 text-gray-400" />
                    </div>

                    <div className="flex gap-3 pt-2">
                        <Button
                            variant="outline"
                            onClick={onCancel}
                            className="flex-1"
                        >
                            キャンセル
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={isProcessing}
                            className="flex-1 bg-black text-white hover:bg-gray-800"
                        >
                            {isProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                            適用
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};
